import { Show, For, createSignal, createMemo, onCleanup, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ScreenInfo {
  id: number;
  name?: string;
  width?: number;
  height?: number;
  primary?: boolean;
  scale?: number;
}

interface ScreensResult {
  screens?: ScreenInfo[];
}

interface ScreenshotResult {
  image?: string;
  data?: string;
  screenId?: number;
  width?: number;
  height?: number;
}

interface ClickResult {
  ok?: boolean;
}

interface TypeResult {
  ok?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

function b64ToImageSrc(b64: string): string {
  if (b64.startsWith("data:image")) return b64;
  return `data:image/png;base64,${b64}`;
}

// ── Panel ──────────────────────────────────────────────────────────────────────

const ComputerusePanel: Component = () => {
  const [screenshotSrc, setScreenshotSrc] = createSignal<string | null>(null);
  const [screenshotMeta, setScreenshotMeta] = createSignal<{
    width?: number;
    height?: number;
    screenId?: number;
  } | null>(null);
  const [capturing, setCapturing] = createSignal(false);
  const [clickX, setClickX] = createSignal<string>("");
  const [clickY, setClickY] = createSignal<string>("");
  const [typeText, setTypeText] = createSignal<string>("");
  const [actionLoading, setActionLoading] = createSignal<string | null>(null);
  const [lastError, setLastError] = createSignal<string>("");
  const [selectedScreenId, setSelectedScreenId] = createSignal<number | null>(null);
  const [presenterMode, setPresenterMode] = createSignal(false);
  const [scrollAmount, setScrollAmount] = createSignal<string>("3");
  const [batchScript, setBatchScript] = createSignal<string>("");
  const [showBatch, setShowBatch] = createSignal(false);
  const [showPeerActions, setShowPeerActions] = createSignal(false);

  const ws = useWs();

  // ── Timeline des actions des autres opérateurs ────────────────────────────
  interface PeerAction {
    peerId: string;
    action: string;
    detail: string;
    ts: string;
  }
  const [peerActions, setPeerActions] = createSignal<PeerAction[]>([]);
  function addPeerAction(ev: { peerId?: string; action?: string; detail?: string; ts?: string }) {
    if (!ev.peerId || ev.peerId === ws.state.myPeerId) return;
    setPeerActions((prev) => {
      const next = [
        { peerId: ev.peerId!, action: ev.action || "", detail: ev.detail || "", ts: ev.ts || new Date().toISOString() },
        ...prev,
      ].slice(0, 50);
      return next;
    });
  }
  const offAudit = ws.on(
    (m) => m.type === "audit:event",
    (m: unknown) => {
      const msg = m as { event?: { action?: string; detail?: string; peerId?: string; ts?: string } };
      const ev = msg.event;
      if (!ev) return;
      if (ev.action === "tool_requested" && ev.detail?.startsWith("computeruse_")) {
        addPeerAction(ev);
      }
    },
  );
  onCleanup(() => offAudit());

  // panel doit être déclaré AVANT screensQuery (TDZ dans createToolQuery).
  const panel = usePanel(() => ({ isFetching: screensQuery.isFetching, error: screensQuery.error }));

  const screensQuery = createToolQuery<ScreensResult>(() => ({
    tool: "computeruse_screens",
    params: {},
    enabled: panel.enabled(),
    refetchInterval: 0,
    staleTime: 30_000,
  }));

  const screens = createMemo<ScreenInfo[]>(() => screensQuery.data?.screens ?? []);

  async function takeScreenshot(): Promise<void> {
    if (capturing()) return;
    setCapturing(true);
    setLastError("");
    try {
      const params: Record<string, unknown> = {};
      const sel = selectedScreenId();
      if (sel != null) params.screenId = sel;
      const res = await panel.tools.call<ScreenshotResult>("computeruse_screenshot", params);
      const raw = res?.image ?? res?.data ?? "";
      if (raw) {
        setScreenshotSrc(b64ToImageSrc(raw));
        setScreenshotMeta({ width: res?.width, height: res?.height, screenId: res?.screenId });
      } else {
        setLastError("Aucune image retournée.");
      }
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setCapturing(false);
    }
  }

  async function sendClick(): Promise<void> {
    const x = Number(clickX());
    const y = Number(clickY());
    if (Number.isNaN(x) || Number.isNaN(y)) {
      setLastError("Coordonnées invalides.");
      return;
    }
    setActionLoading("click");
    setLastError("");
    try {
      await panel.tools.call<ClickResult>("computeruse_click", { x, y });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  async function sendType(): Promise<void> {
    const text = typeText();
    if (!text) return;
    setActionLoading("type");
    setLastError("");
    try {
      await panel.tools.call<TypeResult>("computeruse_type", { text });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  async function sendKey(key: string): Promise<void> {
    setActionLoading("key");
    setLastError("");
    try {
      await panel.tools.call<TypeResult>("computeruse_key", { key });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  async function sendPresenterClick(): Promise<void> {
    const x = Number(clickX());
    const y = Number(clickY());
    if (Number.isNaN(x) || Number.isNaN(y)) {
      setLastError("Coordonnées invalides.");
      return;
    }
    setActionLoading("presenter");
    setLastError("");
    try {
      await panel.tools.call<ClickResult>("computeruse_move", { x, y });
      await new Promise((r) => setTimeout(r, 200));
      await panel.tools.call<ClickResult>("computeruse_click", { x, y });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  async function sendScroll(): Promise<void> {
    const x = Number(clickX());
    const y = Number(clickY());
    const amount = Number(scrollAmount());
    if (Number.isNaN(x) || Number.isNaN(y)) {
      setLastError("Coordonnées invalides.");
      return;
    }
    setActionLoading("scroll");
    setLastError("");
    try {
      await panel.tools.call<ClickResult>("computeruse_scroll", { x, y, amount });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  async function sendBatch(): Promise<void> {
    const script = batchScript().trim();
    if (!script) return;
    setActionLoading("batch");
    setLastError("");
    try {
      await panel.tools.call<ClickResult>("computeruse_batch", { script });
    } catch (err) {
      setLastError(errMessage(err));
    } finally {
      setActionLoading(null);
    }
  }

  const toolbar = (
    <>
      <button class="btn btn--sm btn--primary" onClick={takeScreenshot} disabled={capturing()}>
        {capturing() ? "⏳ Capture…" : "📷 Capture d'écran"}
      </button>
      <button class="btn btn--xs btn--ghost" onClick={() => screensQuery.refetch()} title="Recharger les écrans">
        ↺
      </button>
      <Show when={screens().length > 0}>
        <select
          class="panel-select"
          value={selectedScreenId() ?? ""}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setSelectedScreenId(v ? Number(v) : null);
          }}
        >
          <option value="">Écran par défaut</option>
          <For each={screens()}>
            {(s) => (
              <option value={s.id}>
                {s.name ?? `Écran ${s.id}`} {s.width && s.height ? `(${s.width}×${s.height})` : ""}
                {s.primary ? " ●" : ""}
              </option>
            )}
          </For>
        </select>
      </Show>
      <button
        class="btn btn--xs"
        classList={{ "btn--primary": presenterMode() }}
        onClick={() => setPresenterMode((v) => !v)}
        title={presenterMode() ? "Mode présentateur (déplace + clique)" : "Mode direct (clique immédiat)"}
      >
        {presenterMode() ? "🎤 Présentateur" : "⚡ Direct"}
      </button>
      <button
        class="btn btn--xs btn--ghost"
        classList={{ "btn--primary": showBatch() }}
        onClick={() => setShowBatch((v) => !v)}
        title="Script batch"
      >
        📜
      </button>
      <button
        class="btn btn--xs btn--ghost"
        classList={{ "btn--primary": showPeerActions() }}
        onClick={() => setShowPeerActions((v) => !v)}
        title="Afficher les actions des autres opérateurs"
      >
        👁
      </button>
    </>
  );

  return (
    <PanelLayout title="Computer Use" toolbar={toolbar} status={panel.status()}>
      <div class="cu-panel">
        {/* Erreur */}
        <Show when={lastError()}>
          <div class="cu-banner cu-banner--danger">{lastError()}</div>
        </Show>

        {/* Actions des autres opérateurs */}
        <Show when={showPeerActions() && peerActions().length > 0}>
          <div class="cu-peer-actions">
            <For each={peerActions().slice(0, 3)}>
              {(a) => (
                <div class="cu-peer-action">
                  <span class="cu-peer-id">{a.peerId.slice(0, 6)}…</span>
                  <span class="cu-peer-detail" title={a.detail}>
                    {a.detail.slice(0, 50)}
                  </span>
                  <span class="cu-peer-ts">
                    {new Date(a.ts).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                </div>
              )}
            </For>
          </div>
        </Show>

        {/* Screenshot */}
        <div class="cu-screenshot-section">
          <div class="cu-screenshot-header">
            <strong>Aperçu</strong>
            <Show when={screenshotMeta()}>
              {(m) => (
                <PanelBadge intent="info">
                  {m().width && m().height ? `${m().width}×${m().height}` : ""}
                  {m().screenId != null ? ` · écran ${m().screenId}` : ""}
                </PanelBadge>
              )}
            </Show>
          </div>
          <Show
            when={screenshotSrc()}
            fallback={<div class="cu-screenshot-empty">Aucune capture — cliquez sur « Capture d'écran ».</div>}
          >
            {(src) => <img class="cu-screenshot-img" src={src()} alt="Capture d'écran" />}
          </Show>
        </div>

        {/* Contrôles */}
        <div class="cu-controls">
          <div class="cu-controls-row">
            <strong>Actions</strong>
            <span class="cu-mode-badge">{presenterMode() ? "🎤 Présentateur" : "⚡ Direct"}</span>
          </div>

          {/* Clic / Présentateur */}
          <div class="cu-controls-row">
            <span class="cu-controls-label">{presenterMode() ? "Déplacer+Cliquer" : "Clic"}</span>
            <input
              class="cu-controls-input"
              type="number"
              placeholder="x"
              value={clickX()}
              onInput={(e) => setClickX(e.currentTarget.value)}
              disabled={!!actionLoading()}
            />
            <input
              class="cu-controls-input"
              type="number"
              placeholder="y"
              value={clickY()}
              onInput={(e) => setClickY(e.currentTarget.value)}
              disabled={!!actionLoading()}
            />
            <button
              class="btn btn--sm"
              onClick={() => (presenterMode() ? sendPresenterClick() : sendClick())}
              disabled={!!actionLoading() || !clickX() || !clickY()}
            >
              {actionLoading() === "click" || actionLoading() === "presenter"
                ? "…"
                : presenterMode()
                  ? "🎤 Go"
                  : "Clic"}
            </button>
          </div>

          {/* Scroll */}
          <div class="cu-controls-row">
            <span class="cu-controls-label">Scroll</span>
            <input
              class="cu-controls-input"
              type="number"
              placeholder="x"
              value={clickX()}
              onInput={(e) => setClickX(e.currentTarget.value)}
              disabled={!!actionLoading()}
            />
            <input
              class="cu-controls-input"
              type="number"
              placeholder="y"
              value={clickY()}
              onInput={(e) => setClickY(e.currentTarget.value)}
              disabled={!!actionLoading()}
            />
            <input
              class="cu-controls-input"
              type="number"
              placeholder="lignes"
              value={scrollAmount()}
              onInput={(e) => setScrollAmount(e.currentTarget.value)}
              disabled={!!actionLoading()}
              style={{ width: "56px" }}
            />
            <button class="btn btn--sm" onClick={sendScroll} disabled={!!actionLoading() || !clickX() || !clickY()}>
              {actionLoading() === "scroll" ? "…" : "Scroll"}
            </button>
          </div>

          {/* Texte */}
          <div class="cu-controls-row">
            <span class="cu-controls-label">Taper</span>
            <input
              class="cu-controls-input cu-controls-input--wide"
              type="text"
              placeholder="Texte à taper…"
              value={typeText()}
              onInput={(e) => setTypeText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void sendType();
                }
              }}
              disabled={actionLoading() === "type"}
            />
            <button class="btn btn--sm" onClick={sendType} disabled={actionLoading() === "type" || !typeText().trim()}>
              {actionLoading() === "type" ? "…" : "Taper"}
            </button>
          </div>

          {/* Touches rapides */}
          <div class="cu-controls-row">
            <span class="cu-controls-label">Touches</span>
            <For each={["Enter", "Escape", "Tab", "Space", "Backspace"]}>
              {(key) => (
                <button
                  class="btn btn--xs btn--ghost"
                  onClick={() => sendKey(key)}
                  disabled={actionLoading() === "key"}
                >
                  {key}
                </button>
              )}
            </For>
          </div>
        </div>

        {/* Batch */}
        <Show when={showBatch()}>
          <div class="cu-controls">
            <div class="cu-controls-row">
              <strong>Script batch</strong>
            </div>
            <div class="cu-controls-row">
              <textarea
                class="cu-controls-input cu-controls-input--wide"
                placeholder="click 100 200\ntype 'hello'\nkey Enter\nscroll 100 200 3"
                value={batchScript()}
                onInput={(e) => setBatchScript(e.currentTarget.value)}
                rows={4}
                style={{ resize: "vertical", "min-height": "60px", "font-family": "ui-monospace, monospace" }}
              />
              <button
                class="btn btn--sm"
                onClick={sendBatch}
                disabled={actionLoading() === "batch" || !batchScript().trim()}
              >
                {actionLoading() === "batch" ? "…" : "Exécuter"}
              </button>
            </div>
          </div>
        </Show>

        {/* Liste des écrans */}
        <div class="cu-screens-list">
          <div class="cu-screens-header">
            <strong>Écrans détectés</strong>
            <PanelBadge intent="info">{screens().length}</PanelBadge>
          </div>
          <Show when={screens().length > 0} fallback={<div class="cu-screenshot-empty">Aucun écran détecté.</div>}>
            <ul class="cu-screens-items">
              <For each={screens()}>
                {(s) => (
                  <li class="cu-screen-item">
                    <span class="cu-screen-name">{s.name ?? `Écran ${s.id}`}</span>
                    <span class="cu-screen-meta">
                      {s.width && s.height ? `${s.width}×${s.height}` : ""}
                      {s.scale ? ` · scale ${s.scale}` : ""}
                      {s.primary ? " · primaire" : ""}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </div>

      <style>{`
        .cu-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 8px 4px;
        }
        .cu-banner {
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
        }
        .cu-banner--danger {
          background: rgba(220, 80, 80, 0.08);
          color: #f4c2c2;
          border-left: 2px solid #d05050;
        }
        .cu-screenshot-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px solid var(--border, #333);
          border-radius: 6px;
          padding: 8px;
          background: var(--bg-card, #111);
        }
        .cu-screenshot-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-bottom: 4px;
          border-bottom: 1px solid var(--border, #2a2a2a);
        }
        .cu-screenshot-empty {
          padding: 24px;
          text-align: center;
          color: var(--muted, #888);
          font-style: italic;
          font-size: 12px;
        }
        .cu-screenshot-img {
          max-width: 100%;
          border-radius: 4px;
          border: 1px solid var(--border, #333);
          background: var(--input-bg, #1a1a1a);
        }
        .cu-controls {
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px solid var(--border, #333);
          border-radius: 6px;
          padding: 8px;
          background: var(--bg-card, #111);
        }
        .cu-controls-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .cu-controls-label {
          font-size: 12px;
          color: var(--muted, #888);
          min-width: 48px;
        }
        .cu-controls-input {
          width: 72px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
          padding: 5px 8px;
          border-radius: 4px;
          border: 1px solid var(--border, #444);
          background: var(--input-bg, #1a1a1a);
          color: var(--fg, #eee);
        }
        .cu-controls-input--wide {
          flex: 1;
          min-width: 120px;
        }
        .cu-screens-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px solid var(--border, #333);
          border-radius: 6px;
          padding: 8px;
          background: var(--bg-card, #111);
        }
        .cu-screens-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding-bottom: 4px;
          border-bottom: 1px solid var(--border, #2a2a2a);
        }
        .cu-screens-items {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .cu-screen-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          padding: 4px 6px;
          border-radius: 3px;
          font-size: 12px;
        }
        .cu-screen-item:hover {
          background: var(--hover, rgba(255,255,255,0.04));
        }
        .cu-screen-name {
          color: var(--fg, #eee);
        }
        .cu-screen-meta {
          color: var(--muted, #888);
          font-variant-numeric: tabular-nums;
        }
        .cu-peer-actions {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 4px 8px;
          background: var(--bg-elev, #131c33);
          border: 1px solid var(--border, #333);
          border-radius: 6px;
          font-size: 11px;
        }
        .cu-peer-action {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 2px 4px;
          border-radius: 3px;
        }
        .cu-peer-action:hover {
          background: var(--hover, rgba(255,255,255,0.04));
        }
        .cu-peer-id {
          color: var(--info, #5ba8ff);
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10px;
          min-width: 48px;
        }
        .cu-peer-detail {
          flex: 1;
          color: var(--muted, #888);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cu-peer-ts {
          color: var(--text-3, #6e7ba0);
          font-size: 10px;
        }
      `}</style>
    </PanelLayout>
  );
};

registerPanel("computeruse", ComputerusePanel);

export default ComputerusePanel;
